<template>
  <div class="desktop-betting-section">
    <div class="desktop-betting-header">
      <h2 class="desktop-section-title">
        {{ $t('betting.selectYourMarket') }}
      </h2>
    </div>
    
    <!-- Tab Navigation -->
    <div class="desktop-tab-navigation">
      <button
        class="desktop-tab-btn"
        :class="{ active: activeTab === 'main' }"
        @click="activeTab = 'main'"
      >
        🎯 {{ $t('betting.mainMarkets') }}
      </button>
      <button
        class="desktop-tab-btn"
        :class="{ active: activeTab === 'bonus' }"
        @click="activeTab = 'bonus'"
      >
        🎁 {{ $t('betting.bonusMarkets') }}
      </button>
    </div>
    
    <!-- Main Tab Content -->
    <div
      v-if="activeTab === 'main'"
      class="desktop-tab-content"
    >
      <!-- Market Selection Dropdown -->
      <div class="desktop-market-select-container">
        <select 
          v-model="selectedMainMarket"
          class="desktop-market-dropdown"
          @change="onMainMarketChange"
        >
          <option
            value=""
            disabled
          >
            {{ $t('betting.chooseMarket') }}
          </option>
          <option 
            v-for="market in allMainMarkets" 
            :key="market.id" 
            :value="market.id"
          >
            {{ getMarketDisplayLabel(market) }}
          </option>
        </select>
        <div class="dropdown-arrow">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      <!-- Number Selection Section -->
      <div
        v-if="selectedMainMarketData && showMainNumberSelection"
        class="desktop-number-selection-section"
      >
        <!-- Main Number Grid -->
        <div
          v-if="!isBonusOnlyMarket && !isMainIncludingBonusMarket"
          class="desktop-number-grid"
        >
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="desktop-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers.includes(n),
              cold: coldNumbers.includes(n),
              disabled: (isSameDrumMarket) && selectedBonusNumbers.includes(n),
            }"
            @click="toggleNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Main Including Bonus Number Grid (Single Grid for markets like 'standard-bet-bonus-from-same-drum') -->
        <div
          v-if="isMainIncludingBonusMarket"
          class="desktop-number-grid"
        >
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="desktop-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers.includes(n),
              cold: coldNumbers.includes(n),
            }"
            @click="toggleMainIncludingBonusNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Bonus ball colour (UK49 color combo) -->
        <div
          v-if="isColorComboMarket"
          class="desktop-bonus-section desktop-color-combo-section"
        >
          <BonusBallColorRow v-model="selectedBonusColor" />
        </div>

        <!-- Bonus Ball Selection Section -->
        <div
          v-if="(requiresBonusBall || isSameDrumMarket || isBonusOnlyMarket) && selectedMainMarket && !isMainIncludingBonusMarket && !isColorComboMarket"
          class="desktop-bonus-selection-area"
        >
          <div class="desktop-bonus-header">
            {{ isBonusOnlyMarket ? $t('betting.selectBonusNumber') : $t('betting.selectBonusBall') }}
          </div>
          <div class="desktop-number-grid">
            <div
              v-for="n in bonusBallsMax"
              :key="`bonus-${n}`"
              class="desktop-number-circle bonus-ball"
              :class="{
                selected: selectedBonusNumbers.includes(n),
                hot: hotNumbers.includes(n),
                cold: coldNumbers.includes(n),
                disabled: (isSameDrumMarket) && selectedNumbers.includes(n),
              }"
              @click="toggleBonusNumber(n)"
            >
              {{ n }}
            </div>
          </div>
        </div>

        <!-- Selected Market Info Summary Bar -->
        <div class="desktop-market-summary-bar">
          <div class="summary-left">
            <span v-if="isBonusOnlyMarket">
              {{ selectedBonusNumbers.length > 0 ? $t('betting.selectedBonusBalls', { count: selectedBonusNumbers.length }) : $t('betting.pleaseSelectBonusNumber') }}
            </span>
            <span v-else>
              {{ selectedNumbers.length > 0 ? $t('betting.selectedBalls', { count: selectedNumbers.length }) : $t('betting.pleaseSelectNumbers') }}
            </span>
          </div>
          <div
            v-if="(selectedNumbers.length > 0 || (isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (isColorComboMarket && selectedBonusColor)) && currentOdds"
            class="summary-right"
          >
            {{ formatOdds(currentOdds) }}
          </div>
        </div>

        <div
          v-if="hasAnySelection"
          class="desktop-betting-actions-primary"
        >
          <button
            type="button"
            class="desktop-betting-action-btn-hero"
            :disabled="!canAddLine"
            @click="addLine"
          >
            {{ props.streamlinedSelection ? $t('detail.addToBetslip') : $t('betting.addLine') }}
          </button>
        </div>
        <div class="desktop-betting-actions-secondary">
          <BettingPickActionButton
            variant="quick"
            platform="desktop"
            :pick-options="availablePickCounts"
            :selected-count="quickPickCount"
            :active="lastPickAction === 'quick' && hasAnySelection"
            @update:selected-count="quickPickCount = $event"
            @action="quickPick"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.1"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M16 3h5v5" />
                <path d="m8 16-5 5" />
                <path d="M21 3l-7 7" />
                <path d="M3 8V3h5" />
                <path d="m3 3 7 7" />
                <path d="m14 14 7 7" />
              </svg>
            </template>
            {{ $t('betting.quickPick') }}
          </BettingPickActionButton>
          <BettingPickActionButton
            v-if="shouldShowHotColdActions"
            variant="hot"
            platform="desktop"
            :pick-options="availablePickCounts"
            :selected-count="hotPickCount"
            :active="lastPickAction === 'hot' && hasAnySelection"
            @update:selected-count="hotPickCount = $event"
            @action="selectHotNumbers"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12.7 2.6c.3 2-.3 3.5-1.7 5.2-1 1.2-1.6 2.2-1.6 3.7 0 1.8 1.3 3.1 3 3.1 2.3 0 3.8-1.9 3.8-4.3 0-1.1-.3-2.1-1.1-3.5 2.9 1.6 4.8 4.4 4.8 7.8 0 4.2-3.2 7.4-7.7 7.4-4.1 0-7.2-2.9-7.2-6.9 0-2.8 1.4-4.9 3.6-6.9 1.8-1.7 3-3.1 4.1-5.6z" />
              </svg>
            </template>
            {{ $t('betting.hotNumbers') }}
          </BettingPickActionButton>
          <BettingPickActionButton
            v-if="shouldShowHotColdActions"
            variant="cold"
            platform="desktop"
            :pick-options="availablePickCounts"
            :selected-count="coldPickCount"
            :active="lastPickAction === 'cold' && hasAnySelection"
            @update:selected-count="coldPickCount = $event"
            @action="selectColdNumbers"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.9"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2v20" />
                <path d="m4.9 6.1 14.2 11.8" />
                <path d="m19.1 6.1-14.2 11.8" />
                <path d="M4 12h16" />
              </svg>
            </template>
            {{ $t('betting.coldNumbers') }}
          </BettingPickActionButton>
          <button
            type="button"
            class="desktop-betting-action-btn-secondary desktop-betting-action-btn-secondary--clear"
            @click="clearAll"
          >
            <span
              class="betting-action-btn-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="m19 6-1 14H6L5 6" />
              </svg>
            </span>
            <span>{{ $t('common.clearAll') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Bonus Tab Content -->
    <div
      v-if="activeTab === 'bonus'"
      class="desktop-tab-content"
    >
      <div class="desktop-bonus-markets">
        <div 
          v-for="group in bonusMarkets" 
          :key="group.id"
          class="desktop-bonus-group"
        >
          <div class="desktop-bonus-group-title">
            {{ group.name }}
          </div>
          <div class="desktop-bonus-markets-grid">
            <div 
              v-for="market in group.markets" 
              :key="market.id"
              class="desktop-bonus-market-item"
            >
              <div class="desktop-bonus-market-description">
                {{ getMarketDisplayLabel(market) }}
              </div>
              <SameColorBallsSelector
                v-if="isSameColorBallsMarket(market.slug)"
                :market="market"
                @select="(outcome) => selectSameColorBalls(market, outcome, group)"
              />
              <div
                v-else
                class="desktop-bonus-outcomes"
              >
                <div
                  v-for="outcome in market.outcomes"
                  :key="outcome.id"
                  class="desktop-bonus-outcome"
                  :class="[
                    { selected: isMarketSelected(market, outcome) },
                    getBonusColorClass(outcome)
                  ]"
                  :style="getBonusColorStyle(outcome)"
                  @click="selectBonusMarket(market, outcome, group)"
                >
                  <span class="desktop-bonus-outcome-label">
                    {{ formatOutcomeText(outcome) }}
                  </span>
                  <span class="desktop-bonus-outcome-odds">
                    {{ outcome.odds }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { formatOdds } from '../../utils/currency';
import { getMarketDisplayLabel } from '../../utils/market';
import SameColorBallsSelector from '../SameColorBallsSelector.vue';
import BonusBallColorRow from '../BonusBallColorRow.vue';
import BettingPickActionButton from '../betting/BettingPickActionButton.vue';
import { getAvailablePickCounts, resolveDefaultPickCount, clampToAvailablePickCount } from '@/composables/useAvailablePickCounts';
import {
  isSameColorBallsMarket,
  formatSameColorBetText,
  buildSameColorMarketSelection,
  pickRandomBallColor,
  type BallColorSlug,
} from '@/constants/sameColorBalls';
import {
  isStandardBetPlusColorBonusMarket,
  buildStandardBetPlusColorBonusSelection,
  resolveColorBonusOutcome,
} from '@/constants/standardBetPlusColorBonus';
import { useIntegrationMode } from '@/composables/useIntegrationMode';

interface MarketOutcome {
  id: number;
  game_market_id?: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}

interface Props {
  bettingMarkets: MarketGroup[];
  selectedMarket: SelectedMarket | null;
  hotNumbers?: number[];
  coldNumbers?: number[];
  betslipLength: number;
  streamlinedSelection?: boolean;
  enableHotColdNumbers?: boolean;
}

const props = defineProps<Props>();
const { isLite } = useIntegrationMode();

const emit = defineEmits<{
  'select-market': [market: SelectedMarket];
  'remove-market': [market: SelectedMarket];
  'toggle-group': [index: number];
  'tab-changed': [tab: 'main' | 'bonus'];
  'add-line': [line: { numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }];
  'remove-line': [index: number];
  'add-to-betslip': [lines: Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }>];
  'clear-all': [];
}>();

// Tab state
const activeTab = ref<'main' | 'bonus'>('main');

// Main market selection
const selectedMainMarket = ref<number | ''>('');
const selectedBonusMarkets = ref<Array<{ market: Market; outcome: MarketOutcome }>>([]);

// Number selection state
const selectedNumbers = ref<number[]>([]);
const selectedBonusNumbers = ref<number[]>([]);
const selectedBonusColor = ref<BallColorSlug | null>(null);
const lines = ref<Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }>>([]);

// Quick pick pattern memory
const lastQuickPickPattern = ref<{ mainCount: number; bonusCount: number } | null>(null);

// Computed properties to separate markets
const mainMarkets = computed(() => {
  return props.bettingMarkets.filter(group => 
    group.markets.some(market => 
      market.outcomes.some(outcome => outcome.select_numbers) ||
      isStandardBetPlusColorBonusMarket(market.slug)
    )
  );
});

const bonusMarkets = computed(() => {
  return props.bettingMarkets.map(group => ({
    ...group,
    markets: group.markets.filter(market => 
      market.outcomes.every(outcome => !outcome.select_numbers) &&
      !isStandardBetPlusColorBonusMarket(market.slug)
    )
  })).filter(group => group.markets.length > 0);
});

const allMainMarkets = computed(() => {
  const markets: Market[] = [];
  mainMarkets.value.forEach(group => {
    group.markets.forEach(market => {
      if (market.outcomes.some(outcome => outcome.select_numbers)) {
        markets.push(market);
      } else if (isStandardBetPlusColorBonusMarket(market.slug)) {
        markets.push(market);
      }
    });
  });
  
  return markets.sort((a, b) => {
    const isABonusBallSameDrum = a.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    const isBBonusBallSameDrum = b.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    
    if (isABonusBallSameDrum && !isBBonusBallSameDrum) return 1;
    if (!isABonusBallSameDrum && isBBonusBallSameDrum) return -1;
    return 0;
  });
});

const selectedMainMarketData = computed(() => {
  return allMainMarkets.value.find(market => market.id === selectedMainMarket.value);
});

const selectedMainMarketGroup = computed(() => {
  return mainMarkets.value.find(group => 
    group.markets.some(market => market.id === selectedMainMarket.value)
  );
});

// Ballset calculations
const actualBallsMax = computed(() => {
  if (selectedMainMarketGroup.value?.rules?.length) {
    const mainRule = selectedMainMarketGroup.value.rules.find(rule => rule.type === 'main' || !rule.has_bonus);
    return mainRule ? mainRule.balls_max : 49;
  }
  return 49; // Default
});

const isColorComboMarket = computed(() =>
  isStandardBetPlusColorBonusMarket(selectedMainMarketData.value?.slug ?? '')
);

const showMainNumberSelection = computed(() => {
  if (!selectedMainMarketData.value) return false;
  if (isColorComboMarket.value) return true;
  return selectedMainMarketData.value.outcomes.some((outcome) => outcome.select_numbers);
});

const actualBalls = computed(() => {
  if (isColorComboMarket.value) {
    const picks = selectedMainMarketData.value?.outcomes
      .map((o) => Number(o.pick) || 0)
      .filter((p) => p > 0) ?? [];
    return picks.length > 0 ? Math.min(...picks) : 1;
  }
  if (selectedMainMarketData.value?.outcomes) {
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    return firstOutcome ? firstOutcome.pick : 5;
  }
  return 5; // Default
});

const bonusBallsMax = computed(() => {
  if (isSameDrumMarket.value) {
    return actualBallsMax.value;
  }
  if (selectedMainMarketGroup.value?.rules?.length) {
    const bonusRule = selectedMainMarketGroup.value.rules.find(rule => rule.has_bonus === 1);
    return bonusRule ? bonusRule.balls_max : 20;
  }
  return 20; // Default
});

const maxSelectableBalls = computed(() => {
  if (selectedMainMarketData.value?.outcomes) {
    const picks = selectedMainMarketData.value.outcomes
      .map((outcome) => Number(outcome.pick) || 0)
      .filter((pick) => pick > 0);
    if (picks.length > 0) {
      return Math.max(...picks);
    }
    const maxPick = Math.max(...selectedMainMarketData.value.outcomes
      .filter(outcome => outcome.select_numbers)
      .map(outcome => outcome.pick)
    );
    return maxPick || actualBallsMax.value;
  }
  return actualBallsMax.value;
});

const comboMaxPick = computed(() => {
  if (!selectedMainMarketData.value) return 5;
  const picks = selectedMainMarketData.value.outcomes
    .map((o) => Number(o.pick) || 0)
    .filter((p) => p > 0);
  return picks.length > 0 ? Math.max(...picks) : 5;
});

const requiresBonusBall = computed(() => {
  return selectedMainMarketGroup.value?.has_bonus || false;
});

const isBonusOnlyMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' || 
         slug === 'bonus-ball-only-additional-drum';
});

const availablePickCounts = computed(() =>
  getAvailablePickCounts(
    selectedMainMarketData.value,
    isBonusOnlyMarket.value,
    actualBalls.value
  )
);

const quickPickCount = ref(5);
const hotPickCount = ref(5);
const coldPickCount = ref(5);
const lastPickAction = ref<'quick' | 'hot' | 'cold' | null>(null);

const clearPickAction = () => {
  lastPickAction.value = null;
};

const syncPickCounts = () => {
  const defaultCount = resolveDefaultPickCount(availablePickCounts.value, actualBalls.value);
  quickPickCount.value = clampToAvailablePickCount(quickPickCount.value, availablePickCounts.value) || defaultCount;
  hotPickCount.value = clampToAvailablePickCount(hotPickCount.value, availablePickCounts.value) || defaultCount;
  coldPickCount.value = clampToAvailablePickCount(coldPickCount.value, availablePickCounts.value) || defaultCount;
};

watch([availablePickCounts, actualBalls], syncPickCounts, { immediate: true });

const isSameDrumMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-and-bonus-ball-same-drum' ||
         slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum' ||
         slug === 'regular-numbers-plus-bonus-ball-same-drum';
});

const isMainIncludingBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-bonus-from-same-drum';
});

const canAddLine = computed(() => {
  if (isColorComboMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusColor.value !== null;
  }

  if (isBonusOnlyMarket.value) {
    return selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  
  if (isMainIncludingBonusMarket.value) {
    return selectedNumbers.value.length >= 1 && Boolean(currentOutcome.value);
  }
  
  // Check for duplicate numbers in same drum markets
  if (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) {
    const hasDuplicates = selectedNumbers.value.some(mainNum => 
      selectedBonusNumbers.value.includes(mainNum)
    );
    if (hasDuplicates) {
      return false;
    }
    
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  
  if (requiresBonusBall.value && !isSameDrumMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  return selectedNumbers.value.length > 0 && Boolean(currentOutcome.value);
});

// Computed properties for current odds display
const currentOutcome = computed(() => {
  // If user is in a bonus-only market, we need to match based on bonus ball count (usually 1)
  if (isBonusOnlyMarket.value && selectedBonusNumbers.value.length > 0) {
    return selectedMainMarketData.value?.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedBonusNumbers.value.length
    );
  }

  if (!selectedMainMarketData.value || selectedNumbers.value.length === 0) return null;

  if (isColorComboMarket.value && selectedBonusColor.value) {
    return resolveColorBonusOutcome(
      selectedMainMarketData.value.outcomes,
      selectedBonusColor.value,
      selectedNumbers.value.length
    ) ?? null;
  }

  // For main including bonus markets, use the actual number count
  if (isMainIncludingBonusMarket.value) {
    return selectedMainMarketData.value.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedNumbers.value.length
    );
  }
  
  return selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === selectedNumbers.value.length
  );
});

const currentOdds = computed(() => {
  return currentOutcome.value ? Number(currentOutcome.value.odds) : null;
});

const currentLineMarket = computed<SelectedMarket | undefined>(() => {
  if (selectedMainMarketData.value && selectedMainMarketGroup.value && currentOutcome.value) {
    if (isColorComboMarket.value && selectedBonusColor.value && selectedNumbers.value.length > 0) {
      const selection = buildStandardBetPlusColorBonusSelection(
        selectedNumbers.value,
        currentOutcome.value
      );
      return {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: selection.label,
        odds: selection.odds,
        select_numbers: true,
        balls: selection.picks,
        outcome_id: selection.outcome_id,
        game_market_id: selection.game_market_id,
      };
    }

    return {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(currentOutcome.value),
      odds: Number(currentOutcome.value.odds),
      select_numbers: currentOutcome.value.select_numbers,
      balls: currentOutcome.value.pick,
      outcome_id: currentOutcome.value.id.toString()
    };
  }

  if (selectedMainMarketData.value && hasAnySelection.value) {
    return undefined;
  }

  return props.selectedMarket || undefined;
});

const buildCurrentLine = () => ({
  numbers: [...selectedNumbers.value],
  bonus: (requiresBonusBall.value || (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) || isBonusOnlyMarket.value) ? selectedBonusNumbers.value[0] : undefined,
  bonusColor: isColorComboMarket.value ? selectedBonusColor.value ?? undefined : undefined,
  numBets: 1,
  picks: selectedNumbers.value.length,
  market: currentLineMarket.value
});

const finalizeLine = (line = buildCurrentLine()) => {
  lastQuickPickPattern.value = {
    mainCount: selectedNumbers.value.length,
    bonusCount: selectedBonusNumbers.value.length
  };

  if (props.streamlinedSelection) {
    emit('add-to-betslip', [line]);
  } else {
    lines.value.push(line);
    emit('add-line', line);
  }

  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
};

// Use props.hotNumbers and props.coldNumbers if provided
const hotNumbers = computed(() => {
  if (props.hotNumbers && props.hotNumbers.length > 0) {
    return props.hotNumbers;
  }
  return [];
});

const coldNumbers = computed(() => {
  if (props.coldNumbers && props.coldNumbers.length > 0) {
    return props.coldNumbers;
  }
  return [];
});

const shouldShowHotColdActions = computed(() => {
  return !isLite.value && props.enableHotColdNumbers === true && (hotNumbers.value.length > 0 || coldNumbers.value.length > 0);
});

const hasAnySelection = computed(() => {
  return selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0;
});

// Set default market selection
const setDefaultMainMarket = () => {
  if (allMainMarkets.value.length > 0 && !selectedMainMarket.value) {
    selectedMainMarket.value = allMainMarkets.value[0].id;
    onMainMarketChange();
  }
};

// Watch for changes in allMainMarkets to set default when data is available
watch(allMainMarkets, (newMarkets) => {
  if (newMarkets.length > 0 && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
}, { immediate: true });

// Watch for tab changes to clear main market selection when switching to bonus
watch(activeTab, (newTab) => {
  emit('tab-changed', newTab);
  if (newTab === 'bonus') {
    selectedMainMarket.value = '';
  } else if (newTab === 'main' && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
});

// Initialize default selection
setDefaultMainMarket();

const onMainMarketChange = () => {
  // Clear all number selections when market changes
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
  lines.value = [];
  
  if (selectedMainMarket.value && selectedMainMarketData.value && selectedMainMarketGroup.value) {
    if (isColorComboMarket.value) {
      return;
    }
    // Find the first outcome with select_numbers for this market
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    if (firstOutcome) {
      const marketData: SelectedMarket = {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: formatOutcomeText(firstOutcome),
        odds: Number(firstOutcome.odds),
        select_numbers: firstOutcome.select_numbers,
        balls: firstOutcome.pick,
        outcome_id: firstOutcome.id.toString()
      };
      emit('select-market', marketData);
    }
  }
};

const selectSameColorBalls = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  const label = formatSameColorBetText(outcome);
  const selection = buildSameColorMarketSelection(market, outcome);

  emit('select-market', {
    marketGroup: group,
    market: market,
    label: label,
    select_numbers: outcome.select_numbers,
    balls: outcome.pick,
    ...selection,
  });
};

const updateSelectedMarketForColorCombo = () => {
  if (!selectedMainMarketData.value || !selectedMainMarketGroup.value) return;
  if (!selectedBonusColor.value || selectedNumbers.value.length === 0) return;

  const outcome = resolveColorBonusOutcome(
    selectedMainMarketData.value.outcomes,
    selectedBonusColor.value,
    selectedNumbers.value.length
  );
  if (!outcome) return;

  const selection = buildStandardBetPlusColorBonusSelection(
    selectedNumbers.value,
    outcome
  );

  emit('select-market', {
    marketGroup: selectedMainMarketGroup.value,
    market: selectedMainMarketData.value,
    label: selection.label,
    odds: selection.odds,
    select_numbers: true,
    balls: selection.picks,
    outcome_id: selection.outcome_id,
    game_market_id: selection.game_market_id,
  });
};

const selectBonusMarket = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  // Check if this market/outcome is already selected
  const isAlreadySelected = selectedBonusMarkets.value.some(selected => 
    selected.market.id === market.id &&
    selected.outcome.id === outcome.id
  );
  
  if (isAlreadySelected) {
    // Remove from selection (toggle off)
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(selected => 
      !(selected.market.id === market.id &&
        selected.outcome.id === outcome.id)
    );
    
    // Emit remove event to parent
    emit('remove-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  } else {
    // Twin outcomes (e.g. Even/Odd): switch within the same market, never both selected.
    const siblingSelections = selectedBonusMarkets.value.filter(
      (selected) => selected.market.id === market.id && selected.outcome.id !== outcome.id
    );
    for (const sibling of siblingSelections) {
      emit('remove-market', {
        marketGroup: group,
        market: sibling.market,
        label: formatOutcomeText(sibling.outcome),
        odds: Number(sibling.outcome.odds),
        select_numbers: sibling.outcome.select_numbers,
        balls: sibling.outcome.pick,
        outcome_id: sibling.outcome.id.toString(),
      });
    }
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(
      (selected) => selected.market.id !== market.id
    );
    selectedBonusMarkets.value.push({ market, outcome });

    emit('select-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  }
};

const isMarketSelected = (market: Market, outcome: MarketOutcome): boolean => {
  // Check if this is a bonus market selection
  if (selectedBonusMarkets.value.length > 0) {
    return selectedBonusMarkets.value.some(selected => 
      selected.market.id === market.id &&
      selected.outcome.id === outcome.id
    );
  }
  
  // Check if this is the currently selected main market
  if (selectedMainMarket.value === market.id) {
    return true;
  }
  
  return false;
};

const formatOutcomeText = (outcome: MarketOutcome): string => {
  let text = outcome.text || outcome.id.toString();
  
  // Replace {value} placeholder with specifier value
  if (text.includes('{value}') && outcome.specifier) {
    text = text.replace('{value}', outcome.specifier);
  }
  
  return text;
};

const formatMarketDescription = (market: Market): string => {
  let description = market.description || '';
  
  // Find the first outcome with a specifier to replace {value} in description
  const outcomeWithSpecifier = market.outcomes.find(outcome => outcome.specifier);
  if (outcomeWithSpecifier?.specifier && description && description.includes('{value}')) {
    description = description.replace('{value}', outcomeWithSpecifier.specifier);
  }
  
  return description;
};

const toggleMainIncludingBonusNumber = (number: number) => {
  clearPickAction();
  const index = selectedNumbers.value.indexOf(number);
  
  if (index === -1) {
    if (selectedNumbers.value.length < maxSelectableBalls.value) {
      selectedNumbers.value.push(number);
    }
  } else {
    // Remove number and shift remaining numbers
    selectedNumbers.value.splice(index, 1);
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const toggleNumber = (number: number) => {
  clearPickAction();
  if (isSameDrumMarket.value && selectedBonusNumbers.value.includes(number)) {
    return;
  }
  
  if (selectedNumbers.value.includes(number)) {
    selectedNumbers.value = selectedNumbers.value.filter(n => n !== number);
  } else {
    if (selectedNumbers.value.length < maxSelectableBalls.value) {
      selectedNumbers.value.push(number);
    }
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const toggleBonusNumber = (number: number) => {
  clearPickAction();
  if (isSameDrumMarket.value && selectedNumbers.value.includes(number)) {
    return;
  }
  
  if (selectedBonusNumbers.value.includes(number)) {
    selectedBonusNumbers.value = selectedBonusNumbers.value.filter(n => n !== number);
  } else {
    // Clear existing selection and select new one
    selectedBonusNumbers.value = [number];
  }
};

const shuffleNumbers = (numbers: number[]) => [...numbers].sort(() => Math.random() - 0.5);

const fillSelectionFromPool = (
  preferredPool: number[],
  fallbackPool: number[],
  targetCount: number,
  excludedNumbers: number[] = []
) => {
  const excluded = new Set(excludedNumbers);
  const uniqueNumbers = new Set<number>();

  shuffleNumbers(preferredPool).forEach((num) => {
    if (!excluded.has(num) && uniqueNumbers.size < targetCount) {
      uniqueNumbers.add(num);
    }
  });

  if (uniqueNumbers.size < targetCount) {
    shuffleNumbers(fallbackPool).forEach((num) => {
      if (!excluded.has(num) && uniqueNumbers.size < targetCount) {
        uniqueNumbers.add(num);
      }
    });
  }

  return Array.from(uniqueNumbers).slice(0, targetCount);
};

const getTargetSelectionCounts = (mainCountOverride?: number) => {
  const clampMainCount = (count: number) => Math.min(Math.max(count, 0), maxSelectableBalls.value);

  if (isBonusOnlyMarket.value) {
    return {
      mainCount: 0,
      bonusCount: selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 1,
    };
  }

  if (mainCountOverride != null) {
    return {
      mainCount: clampMainCount(clampToAvailablePickCount(mainCountOverride, availablePickCounts.value)),
      bonusCount: (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
    };
  }

  if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
    return {
      mainCount: clampMainCount(selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value),
      bonusCount:
        selectedBonusNumbers.value.length > 0
          ? selectedBonusNumbers.value.length
          : (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
    };
  }

  if (lastQuickPickPattern.value) {
    return {
      mainCount: clampMainCount(lastQuickPickPattern.value.mainCount),
      bonusCount: lastQuickPickPattern.value.bonusCount,
    };
  }

  return {
    mainCount: clampMainCount(actualBalls.value),
    bonusCount: (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
  };
};

const applyTrendSelection = (preferredPool: number[], fallbackLabel: 'hot' | 'cold', mainCountOverride?: number) => {
  const { mainCount, bonusCount } = getTargetSelectionCounts(mainCountOverride);

  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];

  if (isBonusOnlyMarket.value) {
    const bonusPool = preferredPool.filter((n) => n <= bonusBallsMax.value);
    selectedBonusNumbers.value = fillSelectionFromPool(
      bonusPool,
      Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1),
      bonusCount
    );
    lastQuickPickPattern.value = { mainCount, bonusCount };
    return;
  }

  if (mainCount > 0) {
    const preferredMainPool = preferredPool.filter((n) => n <= actualBallsMax.value);
    selectedNumbers.value = fillSelectionFromPool(
      preferredMainPool,
      Array.from({ length: actualBallsMax.value }, (_, i) => i + 1),
      mainCount
    );
  }

  if (bonusCount > 0) {
    const preferredBonusPool = preferredPool.filter((n) => n <= bonusBallsMax.value);
    const availableBonusPool = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    selectedBonusNumbers.value = fillSelectionFromPool(
      preferredBonusPool,
      availableBonusPool,
      bonusCount,
      isSameDrumMarket.value ? selectedNumbers.value : []
    );
  }

  lastQuickPickPattern.value = { mainCount, bonusCount };
  updateSelectedMarketForBalls(selectedNumbers.value.length);

  if ((mainCount > 0 && selectedNumbers.value.length < mainCount) || (bonusCount > 0 && selectedBonusNumbers.value.length < bonusCount)) {
    console.warn(`Unable to fully apply ${fallbackLabel} number selection pattern; used best available fallback numbers.`);
  }
};

const updateSelectedMarketForBalls = (numberOfBalls: number) => {
  if (!selectedMainMarketData.value || isColorComboMarket.value) return;
  
  // Find the outcome that matches the number of balls selected
  const matchingOutcome = selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === numberOfBalls
  );
  
  if (matchingOutcome && selectedMainMarketGroup.value) {
    // Update the selected market with the new outcome
    const marketData: SelectedMarket = {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(matchingOutcome),
      odds: Number(matchingOutcome.odds),
      select_numbers: matchingOutcome.select_numbers,
      balls: matchingOutcome.pick,
      outcome_id: matchingOutcome.id.toString()
    };
    emit('select-market', marketData);
  }
};

const quickPick = (mainCountOverride?: number) => {
  lastPickAction.value = 'quick';
  if (isBonusOnlyMarket.value) {
    // For bonus-only markets, maintain the same number of bonus selections
    const currentBonusCount = selectedBonusNumbers.value.length;
    const targetBonusCount = currentBonusCount > 0 ? currentBonusCount : 1;
    
    const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    const shuffledBonus = bonusNumbers.sort(() => Math.random() - 0.5);
    selectedBonusNumbers.value = shuffledBonus.slice(0, targetBonusCount);
    
    // Store pattern for bonus-only market
    lastQuickPickPattern.value = { mainCount: 0, bonusCount: targetBonusCount };
  } else {
    // For regular markets, 
    let mainCount: number;
    let bonusCount: number;
    
    // If user has existing selections, maintain the same count but clear previous selections
    if (mainCountOverride != null) {
      mainCount = clampToAvailablePickCount(mainCountOverride, availablePickCounts.value);
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
      mainCount = Math.min(selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value, maxSelectableBalls.value);
      bonusCount = selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 
                   ((requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0);
      
      // Clear previous selections before picking new ones
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (lastQuickPickPattern.value) {
      // Use the same pattern as the last quick pick
      mainCount = Math.min(lastQuickPickPattern.value.mainCount, maxSelectableBalls.value);
      bonusCount = lastQuickPickPattern.value.bonusCount;
    } else {
      // First time - use current market requirements
      mainCount = Math.min(actualBalls.value, maxSelectableBalls.value);
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
    }
    
    // Quick pick main numbers
    if (mainCount > 0) {
      const availableNumbers = Array.from({ length: actualBallsMax.value }, (_, i) => i + 1);
      const shuffled = availableNumbers.sort(() => Math.random() - 0.5);
      selectedNumbers.value = shuffled.slice(0, mainCount);
    }
    
    // Quick pick bonus numbers (if required)
    if (bonusCount > 0) {
      const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
      const availableBonusNumbers = isSameDrumMarket.value 
        ? bonusNumbers.filter(num => !selectedNumbers.value.includes(num))
        : bonusNumbers;
      
      if (availableBonusNumbers.length > 0) {
        const shuffledBonus = availableBonusNumbers.sort(() => Math.random() - 0.5);
        selectedBonusNumbers.value = shuffledBonus.slice(0, bonusCount);
      } else {
        selectedBonusNumbers.value = [];
      }
    }
    
    // Store the pattern for next quick pick
    lastQuickPickPattern.value = { mainCount, bonusCount };

    if (isColorComboMarket.value) {
      selectedBonusColor.value = pickRandomBallColor();
    }
    
    // Update market selection to match the number of balls selected
    if (isColorComboMarket.value) {
      updateSelectedMarketForColorCombo();
    } else {
      updateSelectedMarketForBalls(selectedNumbers.value.length);
    }
  }

};

const addLine = () => {
  if (!canAddLine.value) return;
  finalizeLine();
};

const selectHotNumbers = (mainCountOverride?: number) => {
  const pool = [...hotNumbers.value];
  if (!pool.length) return;
  lastPickAction.value = 'hot';
  applyTrendSelection(pool, 'hot', mainCountOverride);

};

const selectColdNumbers = (mainCountOverride?: number) => {
  const pool = [...coldNumbers.value];
  if (!pool.length) return;
  lastPickAction.value = 'cold';
  applyTrendSelection(pool, 'cold', mainCountOverride);

};

const bonusColorMap: Record<string, string> = {
  blue: '#3B82F6',
  brown: '#8B5E3C',
  green: '#28AA23',
  orange: '#F28C28',
  purple: '#7C3AED',
  red: '#E53935',
  yellow: '#EAB308',
};

const getBonusColorKey = (outcome: MarketOutcome) => {
  const normalized = outcome.text.trim().toLowerCase();
  return Object.keys(bonusColorMap).find((key) => normalized.startsWith(key)) || '';
};

const getBonusColorClass = (outcome: MarketOutcome) => {
  return getBonusColorKey(outcome) ? 'desktop-bonus-outcome--bonus-color' : '';
};

const getBonusColorStyle = (outcome: MarketOutcome) => {
  const key = getBonusColorKey(outcome);
  return key ? { '--bonus-color-swatch': bonusColorMap[key] } : {};
};

watch(selectedBonusColor, () => {
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeLine = (index: number) => {
  lines.value.splice(index, 1);
  emit('remove-line', index);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearAll = () => {
  lines.value = [];
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  emit('clear-all');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const addToBetslip = () => {
  emit('add-to-betslip', lines.value);
};

// Reset quick pick pattern when market changes
watch(selectedMainMarket, () => {
  lastQuickPickPattern.value = null;
});

watch(() => props.betslipLength, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    selectedBonusMarkets.value = [];
  }
});

// Reset internal state when market is cleared
watch(() => props.selectedMarket, (newMarket) => {
  if (!newMarket) {
    // Reset all internal state when market is cleared
    selectedNumbers.value = [];
    selectedBonusNumbers.value = [];
    lines.value = [];
    lastQuickPickPattern.value = null;
  }
});
</script>

<script lang="ts">
export default {
  name: "DesktopBettingSection",
};
</script>

<style scoped>
.desktop-betting-section {
  --trend-hot-base: var(--detail-trend-hot-base, var(--colour-yellow-text-colour));
  --trend-cold-base: var(--detail-trend-cold-base, var(--colour-surface-cont-low));
  --trend-neutral-shadow: color-mix(in srgb, var(--shadow-color) 68%, transparent);
  --trend-hot-ring: color-mix(in srgb, var(--trend-hot-base) 72%, var(--colour-surface-cont-high) 28%);
  --trend-cold-ring: color-mix(in srgb, var(--trend-cold-base) 72%, var(--colour-surface-cont-high) 28%);
  --trend-hot-text: color-mix(in srgb, var(--trend-hot-base) 74%, var(--colour-base-text-colour) 26%);
  --trend-cold-text: color-mix(in srgb, var(--trend-cold-base) 74%, var(--colour-base-text-colour) 26%);
  --trend-ball-surface: color-mix(in srgb, var(--colour-surface) 88%, white 12%);
  --trend-ball-shadow: color-mix(in srgb, var(--shadow-color) 28%, transparent);
  --trend-ball-border: color-mix(in srgb, var(--colour-surface-cont) 78%, var(--colour-base-text-colour) 22%);
  --trend-quick-soft: color-mix(in srgb, var(--colour-orange-button) 14%, var(--colour-surface-cont-low) 86%);
  --trend-hot-soft: color-mix(in srgb, var(--trend-hot-base) 16%, var(--colour-surface-cont-low) 84%);
  --trend-cold-soft: color-mix(in srgb, var(--trend-cold-base) 16%, var(--colour-surface-cont-low) 84%);
  --trend-clear-soft: color-mix(in srgb, var(--colour-secondary-text-colour) 10%, var(--colour-surface-cont-low) 90%);
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--detail-panel-radius, 4px);
  margin-bottom: 12px;
  overflow: hidden;
  border: var(--detail-panel-border-width, 1px) solid var(--colour-surface-cont);
}

.desktop-betting-header {
  padding: 12px 16px;
  background: var(--surface-detail-header);
}

.desktop-section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-detail-header);
  margin: 0;
}

.desktop-tab-navigation {
  display: flex;
  background: var(--surface-detail-panel);
  border-bottom: 1px solid var(--border-detail-card);
}

.desktop-tab-btn {
  flex: 1;
  padding: 16px 24px;
  background: transparent;
  border: none;
  color: var(--text-detail-body);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease;
  border-bottom: 3px solid transparent;
}

.desktop-tab-btn.active {
  color: var(--text-detail-body);
  background: transparent;
  border-bottom-color: var(--colour-orange-button);
}

.desktop-tab-content {
  padding: 24px;
  background: var(--colour-surface-cont-lowest);
}

.desktop-market-select-container {
  position: relative;
  width: 100%;
  margin-bottom: 24px;
}

.desktop-market-dropdown {
  width: 100%;
  padding: 14px 44px 14px 16px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: var(--colour-surface-cont-lowest);
  border: 1px solid var(--detail-market-select-border, var(--colour-surface-cont));
  border-radius: 4px;
  font-weight: 700;
  font-size: 16px;
  color: var(--colour-base-text-colour);
  cursor: pointer;
  text-align: center;
}

.desktop-market-dropdown:focus {
  outline: none;
  border-color: var(--colour-orange-button);
}

.dropdown-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--colour-base-text-colour);
}

.desktop-number-selection-section {
  margin-bottom: 0;
}

.desktop-number-grid {
  display: grid;
  grid-template-columns: repeat(15, 1fr);
  gap: 10px;
  margin-bottom: 24px;
}

.desktop-number-circle {
  aspect-ratio: 1/1;
  position: relative;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  border: 2px solid var(--detail-number-border, var(--trend-ball-border));
  background: var(--colour-surface-cont-lowest);
  color: var(--detail-number-color, var(--colour-base-text-colour));
  box-shadow: none;
}

.desktop-number-circle:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--colour-orange-button) 68%, var(--trend-ball-border) 32%);
}

.desktop-number-circle.selected {
  background: var(--detail-number-selected-bg, var(--colour-orange-button));
  border-color: var(--detail-number-selected-bg, var(--colour-orange-button));
  color: var(--detail-number-selected-color, var(--text-button));
}

.desktop-number-circle.hot {
  border-color: color-mix(in srgb, var(--trend-hot-ring) 72%, var(--trend-ball-border) 28%);
  background: color-mix(in srgb, var(--trend-hot-base) 8%, var(--colour-surface) 92%);
}

.desktop-number-circle.cold {
  border-color: color-mix(in srgb, var(--trend-cold-ring) 72%, var(--trend-ball-border) 28%);
  background: color-mix(in srgb, var(--trend-cold-base) 8%, var(--colour-surface) 92%);
}

.desktop-number-circle.hot::after,
.desktop-number-circle.cold::after {
  position: absolute;
  top: -1px;
  right: 4px;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0;
  border: 2px solid var(--colour-surface-cont-lowest);
  box-shadow: none;
}

.desktop-number-circle.hot::after {
  content: "";
  background: linear-gradient(180deg, color-mix(in srgb, var(--trend-hot-base) 86%, white 14%), color-mix(in srgb, var(--trend-hot-base) 72%, black 28%));
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M12.7 2.6c.3 2-.3 3.5-1.7 5.2-1 1.2-1.6 2.2-1.6 3.7 0 1.8 1.3 3.1 3 3.1 2.3 0 3.8-1.9 3.8-4.3 0-1.1-.3-2.1-1.1-3.5 2.9 1.6 4.8 4.4 4.8 7.8 0 4.2-3.2 7.4-7.7 7.4-4.1 0-7.2-2.9-7.2-6.9 0-2.8 1.4-4.9 3.6-6.9 1.8-1.7 3-3.1 4.1-5.6z'/%3E%3C/svg%3E") center / 11px 11px no-repeat;
}

.desktop-number-circle.cold::after {
  content: "";
  background: var(--trend-cold-base);
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cg fill='none' stroke='white' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M12 2v20'/%3E%3Cpath d='m4.9 6.1 14.2 11.8'/%3E%3Cpath d='m19.1 6.1-14.2 11.8'/%3E%3Cpath d='M4 12h16'/%3E%3C/g%3E%3C/svg%3E") center / 11px 11px no-repeat;
}

.desktop-number-circle.selected.hot {
  background: var(--trend-hot-base);
  border-color: var(--trend-hot-base);
}

.desktop-number-circle.selected.cold {
  background: var(--trend-cold-base);
  border-color: var(--trend-cold-base);
  color: var(--colour-text-inverse);
}

.desktop-bonus-selection-area {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--colour-surface-cont);
}

.desktop-bonus-header {
  font-size: 16px;
  font-weight: 700;
  color: var(--colour-base-text-colour);
  margin-bottom: 16px;
  text-align: center;
  text-transform: uppercase;
}

.desktop-market-summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--colour-surface-cont-lowest);
  padding: 14px 20px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 16px;
}

.summary-left {
  color: var(--colour-base-text-colour);
}

.summary-right {
  color: var(--colour-base-text-colour);
}

.desktop-betting-actions-primary {
  margin-top: 16px;
  margin-bottom: 10px;
}

.desktop-betting-action-btn-hero {
  width: 100%;
  min-height: 48px;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--colour-orange-button) 88%, white 12%), color-mix(in srgb, var(--colour-orange-button) 74%, black 26%));
  color: var(--text-button);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 12px 24px color-mix(in srgb, var(--shadow-color) 95%, transparent);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, filter 0.15s ease;
}

.desktop-betting-action-btn-hero:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.03);
  box-shadow: 0 16px 28px color-mix(in srgb, var(--shadow-color) 100%, transparent);
}

.desktop-betting-action-btn-hero:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.desktop-betting-actions-secondary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 4px;
  margin-top: 10px;
}

/* Secondary row: clearly tappable, still visually below the solid Add line CTA */
.desktop-betting-action-btn-secondary {
  min-height: 44px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid var(--detail-secondary-action-border, var(--bg-secondary-button-border-color, var(--colour-surface-cont)));
  background: var(--colour-surface-cont-lowest);
  color: var(--detail-secondary-action-color, var(--bg-secondary-button-color, var(--colour-base-text-colour)));
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    0 4px 12px color-mix(in srgb, var(--shadow-color) 26%, transparent);
  transition:
    transform 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.desktop-betting-action-btn-secondary:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--colour-surface-cont-low) 88%, var(--bg-hover) 12%);
  box-shadow: 0 8px 18px color-mix(in srgb, var(--shadow-color) 32%, transparent);
}

.desktop-betting-action-btn-secondary:focus-visible {
  outline: 2px solid var(--colour-orange-button);
  outline-offset: 2px;
}

.desktop-betting-action-btn-secondary--quick {
  border-color: var(--bg-secondary-button-border-color, var(--colour-surface-cont));
}

.desktop-betting-action-btn-secondary--quick:hover {
  color: color-mix(in srgb, var(--colour-orange-button) 78%, var(--colour-base-text-colour) 22%);
}

.desktop-betting-action-btn-secondary--hot {
  color: var(--trend-hot-text);
}

.desktop-betting-action-btn-secondary--hot:hover {
  color: color-mix(in srgb, var(--trend-hot-base) 86%, var(--colour-base-text-colour) 14%);
}

.desktop-betting-action-btn-secondary--cold {
  color: var(--trend-cold-base);
}

.desktop-betting-action-btn-secondary--cold:hover {
  color: color-mix(in srgb, var(--trend-cold-base) 86%, var(--colour-base-text-colour) 14%);
}

.desktop-betting-action-btn-secondary--clear {
  color: color-mix(in srgb, var(--colour-base-text-colour) 88%, var(--colour-secondary-text-colour) 12%);
}

.desktop-betting-action-btn-secondary--clear:hover {
  color: var(--colour-base-text-colour);
}

.betting-action-btn-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.betting-action-btn-icon svg {
  width: 16px;
  height: 16px;
}

/* Bonus markets styles */
.desktop-bonus-markets {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.desktop-bonus-group {
  display: flex;
  flex-direction: column;
}

.desktop-bonus-group-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--colour-base-text-colour);
  margin-bottom: 16px;
}

.desktop-bonus-markets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.desktop-bonus-market-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.desktop-bonus-market-description {
  font-size: 14px;
  font-weight: 500;
  color: var(--colour-base-text-colour);
  margin-bottom: 4px;
}

.desktop-bonus-outcomes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.desktop-bonus-outcome {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.desktop-bonus-outcome:hover {
  border-color: var(--colour-orange-button);
}

.desktop-bonus-outcome.selected {
  border-color: var(--colour-orange-button);
  background: var(--colour-surface-cont);
}

.desktop-bonus-outcome--bonus-color {
  background: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface)) 12%, var(--colour-surface) 88%);
  border-color: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface-cont)) 38%, var(--colour-surface-cont) 62%);
}

.desktop-bonus-outcome--bonus-color:hover {
  border-color: var(--bonus-color-swatch, var(--colour-orange-button));
}

.desktop-bonus-outcome--bonus-color.selected {
  background: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface-cont)) 18%, var(--colour-surface-cont) 82%);
  border-color: var(--bonus-color-swatch, var(--colour-orange-button));
}

.desktop-bonus-outcome-label {
  font-size: 15px;
  color: var(--colour-base-text-colour);
}

.desktop-bonus-outcome--bonus-color .desktop-bonus-outcome-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.desktop-bonus-outcome--bonus-color .desktop-bonus-outcome-label::before {
  content: "";
  width: 14px;
  height: 14px;
  border-radius: 999px;
  flex-shrink: 0;
  background: var(--bonus-color-swatch, var(--colour-surface-cont));
  border: 1px solid color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface-cont)) 72%, var(--colour-surface-cont-high) 28%);
}

.desktop-bonus-outcome-odds {
  font-size: 15px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}
</style>
